import React from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import C from "classnames";
import { Tooltip } from "primereact/tooltip";
import { buildRoute } from "../../../../constants/routes";
import { useSmartNavigation } from "../../../../helpers/navigation";
import { isEmpty } from "../../../../utils";
import { getContentTagForDisplay, getVariantStyle } from "../../../utils";

import { buildLessonButtonText, getLessonBadgeSvg } from "../contentCardHelpers";
import { ContentCardProps } from "../contentCardTypes";

import S from "./LessonCard.module.scss";

export const LessonCard: React.FC<ContentCardProps> = ({
  contentType,
  variant,
  points = 100,
  title,
  description,
  difficulty,
  estimatedTime,
  progress,
  status = "notStarted",
  lessonKey,
  inPath = false,
  disabled = false,
  required = true,
}) => {
  const { navigateTo } = useSmartNavigation();

  const navigateToContent = (): void => {
    if (contentType === "exercise") {
      navigateTo(
        buildRoute(
          "Learn.routes.exercise.route",
          { "*": lessonKey || "" },
          { ...(inPath && { inPath: "true" }) }
        )
      );
    } else {
      navigateTo(
        buildRoute(
          "Learn.routes.lesson.route",
          { lessonKey: lessonKey || "" },
          { ...(inPath && { inPath: "true" }) }
        )
      );
    }
  };

  const variantStyle = getVariantStyle(variant as string);

  const buttonText = buildLessonButtonText(contentType, status);
  const badgeSvg = getLessonBadgeSvg(contentType, status);

  const statusClass =
    status === "completed" ? S.lessonCompleted : status === "inProgress" ? S.lessonInProgress : "";

  return (
    <div className={C(S.lessonCard, statusClass, variantStyle)}>
      <Tooltip target={`.${S.optionalIcon}`} content="Optional lesson" position="top" />
      <div className={S.lessonCardHeader}>
        <Tag value={getContentTagForDisplay(contentType)} rounded={true} />
        {points && points > 0 && <h5 className={C(S.mutedText, "atlas-h5")}> +{points}</h5>}
        {!required && <i className={C(S.optionalIcon, "pi pi-lightbulb")} />}
      </div>
      <h4 className={C("atlas-h4", "leading-none")}>{title}</h4>
      <p className={C("body-md", S.description, S.mutedText)}>{description}</p>
      <div className={S.lessonDetailsWrapper}>
        {difficulty && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Difficulty</p>
            <p className="body-md">{difficulty}</p>
          </div>
        )}
        {estimatedTime && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Estimated Time</p>
            <p className="body-md">~ {estimatedTime} mins</p>
          </div>
        )}
        {progress && !isEmpty(progress) && (
          <div>
            <p className={C(S.mutedText, "body-sm")}>Progress</p>
            <p className="body-md">
              {progress.completed}/{progress.totalLessons}
              Tasks
            </p>
          </div>
        )}
      </div>
      <div className={S.lessonCardFooter}>
        <Button label={buttonText} onClick={navigateToContent} disabled={disabled} />
      </div>
      {badgeSvg && (
        <div className={S.badgeSvgWrapper}>
          <img src={badgeSvg} alt={`${contentType} badge`} />
        </div>
      )}
    </div>
  );
};
